// Sidebar.jsx
import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsOwner, selectIsCompanyAdmin, selectIsStaffOrAbove } from "../../store/slices/authSlice";
import { useJourneyProgress } from "../../hooks/useJourneyProgress";
import "./Layout.css";
import logo from "../../assets/images/logo.png";

export default function Sidebar() {
  const isOwner = useSelector(selectIsOwner);
  const isCompanyAdmin = useSelector(selectIsCompanyAdmin);
  const isStaffOrAbove = useSelector(selectIsStaffOrAbove);
  const location = useLocation();
  // const isCompetencyRoute = location.pathname.includes('/competencies');
  // const [competencyOpen, setCompetencyOpen] = useState(isCompetencyRoute);

  // Get journey progress
  const { valuesComplete, diagnosticsComplete, step2Unlocked, step3Unlocked, step4Unlocked, isLoading } = useJourneyProgress();

  // Handle locked item click
  const handleLockedClick = (e, stepName) => {
    e.preventDefault();
    // Could show a toast here: "Complete Values forms to unlock {stepName}"
  };

  return (
    <aside className="ls-sidebar">
      <div className="ls-logo-wrap">
        <div className="ls-logo">
          <img src={logo} alt="Legacy Seventy2 Logo" className="logo-img" />
        </div>
      </div>

      <div className="ls-sections">
        {/* Dashboard - Always Accessible */}
        <section className="ls-block">
          <nav className="ls-nav">
            <NavLink to="/dashboard" className={({isActive}) => isActive ? "active" : undefined}>
              <IconDashboard /> <span>Dashboard</span>
            </NavLink>
          </nav>
        </section>

        {/* Journey Section */}
        <section className="ls-block">
          <div className="ls-block-title">Your Journey</div>
          <nav className="ls-nav ls-journey-nav">
            {/* Step 1: Values - Always Unlocked */}
            <NavLink
              to="/values"
              className={({isActive}) => `ls-step ${isActive ? "active" : ""} ${valuesComplete ? "completed" : ""}`}
            >
              <span className="ls-step-number">1</span>
              <IconCheck />
              <span>Values</span>
              {valuesComplete && <IconCheckCircle className="ls-step-status" />}
            </NavLink>

            {/* Step 2: Diagnostics */}
            {step2Unlocked ? (
              <NavLink
                to="/diagnostic"
                className={({isActive}) => `ls-step ${isActive ? "active" : ""} ${diagnosticsComplete ? "completed" : ""}`}
              >
                <span className="ls-step-number">2</span>
                <IconSteth />
                <span>Diagnostic</span>
                {diagnosticsComplete && <IconCheckCircle className="ls-step-status" />}
              </NavLink>
            ) : (
              <div
                className="ls-step locked"
                onClick={(e) => handleLockedClick(e, "Diagnostic")}
              >
                <span className="ls-step-number">2</span>
                <IconSteth />
                <span>Diagnostic</span>
                <IconLock className="ls-step-lock" />
              </div>
            )}

            {/* Step 3: Competencies */}
            {step3Unlocked ? (
              <NavLink
                to="/competencies-list"
                className={({isActive}) => `ls-step ${isActive ? "active" : ""}`}
              >
                <span className="ls-step-number">3</span>
                <IconLayers />
                <span>Competencies</span>
              </NavLink>
            ) : (
              <div
                className="ls-step locked"
                onClick={(e) => handleLockedClick(e, "Competencies")}
              >
                <span className="ls-step-number">3</span>
                <IconLayers />
                <span>Competencies</span>
                <IconLock className="ls-step-lock" />
              </div>
            )}

            {/* Step 4: Development Plans */}
            {step4Unlocked ? (
              <NavLink
                to="/development-plans"
                className={({isActive}) => `ls-step ${isActive ? "active" : ""}`}
              >
                <span className="ls-step-number">4</span>
                <IconDoc />
                <span>Development Plans</span>
              </NavLink>
            ) : (
              <div
                className="ls-step locked"
                onClick={(e) => handleLockedClick(e, "Development Plans")}
              >
                <span className="ls-step-number">4</span>
                <IconDoc />
                <span>Development Plans</span>
                <IconLock className="ls-step-lock" />
              </div>
            )}
          </nav>
        </section>

        {/* Other Navigation - Always Accessible */}
        <section className="ls-block">
          <nav className="ls-nav">
            <NavLink to="/learning-library" className={({isActive}) => isActive ? "active" : undefined}>
              <IconLibrary /> <span>Learning Library</span>
            </NavLink>
            <NavLink to="/communities" className={({isActive}) => isActive ? "active" : undefined}>
              <IconUsers /> <span>Communities</span>
            </NavLink>
            <NavLink to="/surveys" className={({isActive}) => isActive ? "active" : undefined}>
              <IconSurvey /> <span>Surveys</span>
            </NavLink>
          </nav>
        </section>

        {/* Other Section */}
        <section className="ls-block ls-block-bottom">
          <div className="ls-block-title">Other</div>
          <nav className="ls-nav">
            <NavLink to="/analytics" className={({isActive}) => isActive ? "active" : undefined}>
              <IconChart /> <span>Analytics</span>
            </NavLink>
            {(isOwner || isCompanyAdmin) && (
              <NavLink to="/team" className={({isActive}) => isActive ? "active" : undefined}>
                <IconTeam /> <span>Team</span>
              </NavLink>
            )}
            {(isOwner || isCompanyAdmin || isStaffOrAbove) && (
              <NavLink to="/diagnostic-management" className={({isActive}) => isActive ? "active" : undefined}>
                <IconClipboard /> <span>Manage Diagnostics</span>
              </NavLink>
            )}
            <NavLink to="/settings" className={({isActive}) => isActive ? "active" : undefined}>
              <IconSettings /> <span>Settings</span>
            </NavLink>
            <NavLink to="/help" className={({isActive}) => isActive ? "active" : undefined}>
              <IconHelp /> <span>Help &amp; Support</span>
            </NavLink>
            <NavLink to="/logout" className={({isActive}) => isActive ? "active" : undefined}>
              <IconPower /> <span>Logout</span>
            </NavLink>
          </nav>
        </section>
      </div>
    </aside>
  );
}

/* --------- tiny inline icons (no extra lib) ---------- */
const base = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };

function IconDashboard(){ return (
  <svg {...base}><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/></svg>
);}
function IconCheck(){ return (<svg {...base}><polyline points="20 6 9 17 4 12"/></svg>); }
function IconLayers(){ return (<svg {...base}><polygon points="12 2 22 8 12 14 2 8 12 2"/><polyline points="2 12 12 18 22 12"/></svg>); }
function IconChevron({ className }){ return (<svg {...base} className={className}><polyline points="6 9 12 15 18 9"/></svg>); }
function IconSteth(){ return (<svg {...base}><circle cx="8" cy="8" r="3"/><path d="M8 11v2a4 4 0 0 0 8 0V8"/><circle cx="18" cy="8" r="2"/></svg>); }
function IconDoc(){ return (<svg {...base}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>); }
function IconLibrary(){ return (<svg {...base}><path d="M4 19V5a2 2 0 0 1 2-2h0"/><rect x="6" y="3" width="4" height="18" rx="1"/><rect x="12" y="6" width="4" height="15" rx="1"/><rect x="18" y="9" width="4" height="12" rx="1"/></svg>); }
function IconUsers(){ return (<svg {...base}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>); }
function IconChart(){ return (<svg {...base}><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="9" width="3" height="9"/><rect x="17" y="5" width="3" height="13"/></svg>); }
function IconSettings(){ return (<svg {...base}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 .6 1.65 1.65 0 0 1-2 0 1.65 1.65 0 0 0-1-.6 1.65 1.65 0 0 0-1.82.33l-.06.06A2 2 0 1 1 7 5.29l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 1 2 0 1.65 1.65 0 0 0 1 .6 1.65 1.65 0 0 0 1.82-.33l.06-.06A2 2 0 1 1 20.71 7l-.06.06A1.65 1.65 0 0 0 19.4 9c.22.29.42.62.6 1a1.65 1.65 0 0 1 0 2c-.18.38-.38.71-.6 1z"/></svg>); }
function IconHelp(){ return (<svg {...base}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 1 1 5.82 1c0 2-3 2-3 4"/><line x1="12" y1="17" x2="12" y2="17"/></svg>); }
function IconPower(){ return (<svg {...base}><line x1="12" y1="2" x2="12" y2="12"/><path d="M5.5 5.5a7 7 0 1 0 12.99 0"/></svg>); }
function IconLock({ className }){ return (<svg {...base} className={className}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>); }
function IconCheckCircle({ className }){ return (<svg {...base} className={className}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>); }
function IconTeam(){ return (<svg {...base}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>); }
function IconClipboard(){ return (<svg {...base}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>); }
function IconSurvey(){ return (<svg {...base}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><line x1="9" y1="10" x2="15" y2="10"/><line x1="9" y1="14" x2="15" y2="14"/><line x1="9" y1="18" x2="13" y2="18"/></svg>); }
